<template>
  <el-dialog
    :title="title"
    :visible.sync="dialogVisible"
    width="860px"
    append-to-body
    :close-on-click-modal="false"
    @closed="reset"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      label-width="135px"
      v-loading="loading"
    >
      <el-form-item label="雇主险保单号" prop="policy_no">
        <el-input
          v-model.trim="form.policy_no"
          :disabled="readonly"
          @blur="loadOrderByPolicyNo"
        />
      </el-form-item>
      <el-descriptions v-if="order.id" :column="4" border class="order-summary">
        <el-descriptions-item label="公司名称">{{
          order.company_name || order.policyholder
        }}</el-descriptions-item>
        <el-descriptions-item label="起保时间">{{
          formatDate(order.effect_date)
        }}</el-descriptions-item>
        <el-descriptions-item label="终保时间">{{
          formatDate(order.expire_date)
        }}</el-descriptions-item>
        <el-descriptions-item label="保费">{{
          money(order.pay_price)
        }}</el-descriptions-item>
        <el-descriptions-item label="搬运工人">{{
          order.driver_number || 0
        }}</el-descriptions-item>
        <el-descriptions-item label="文员内勤">{{
          order.clerk_number || 0
        }}</el-descriptions-item>
        <el-descriptions-item label="司机押运">{{
          order.trucker_number || 0
        }}</el-descriptions-item>
        <el-descriptions-item label="保单状态">{{
          orderStatus(order.status)
        }}</el-descriptions-item>
      </el-descriptions>
      <el-row :gutter="20">
        <el-col :span="12"
          ><el-form-item label="出险员工姓名" prop="employee_name"
            ><el-input
              v-model.trim="form.employee_name"
              :disabled="readonly"/></el-form-item
        ></el-col>
        <el-col :span="12"
          ><el-form-item label="出险时间"
            ><el-date-picker
              v-model="form.accident_date"
              type="datetime"
              value-format="yyyy-MM-ddTHH:mm:ssZ"
              :disabled="readonly"
              style="width:100%"/></el-form-item
        ></el-col>
        <el-col :span="12"
          ><el-form-item label="出险员工类别" prop="employee_type"
            ><el-radio-group v-model="form.employee_type" :disabled="readonly"
              ><el-radio :label="1">搬运工人</el-radio
              ><el-radio :label="2">文员内勤</el-radio
              ><el-radio :label="4">司机押运</el-radio></el-radio-group
            ></el-form-item
          ></el-col
        >
        <el-col :span="12"
          ><el-form-item label="案件状态" prop="status"
            ><el-radio-group v-model="form.status" :disabled="readonly"
              ><el-radio :label="1">已决</el-radio
              ><el-radio :label="2">未决</el-radio></el-radio-group
            ></el-form-item
          ></el-col
        >
        <el-col :span="12"
          ><el-form-item label="已赔金额(元)"
            ><el-input-number
              v-model="form.amount_paid"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width:100%"/></el-form-item
        ></el-col>
        <el-col :span="12"
          ><el-form-item label="估损金额(元)"
            ><el-input-number
              v-model="form.amount_loss"
              :min="0"
              :precision="2"
              :controls="false"
              :disabled="readonly"
              style="width:100%"/></el-form-item
        ></el-col>
      </el-row>
      <el-form-item label="事故描述"
        ><el-input
          v-model="form.accident_note"
          type="textarea"
          :rows="6"
          :disabled="readonly"
      /></el-form-item>
    </el-form>
    <span slot="footer"
      ><el-button @click="dialogVisible = false">{{
        readonly ? "关闭" : "取消"
      }}</el-button
      ><el-button
        v-if="!readonly"
        type="primary"
        :loading="submitting"
        @click="submit"
        >保存</el-button
      ></span
    >
  </el-dialog>
</template>

<script>
import dayjs from "dayjs";
import {
  getEmployerClaimList,
  getEmployerOrderList,
  saveEmployerClaim
} from "@/api/employer-insurance";

const emptyForm = () => ({
  id: 0,
  order_id: "",
  policy_no: "",
  employee_name: "",
  employee_type: "",
  accident_date: "",
  accident_note: "",
  amount_paid: undefined,
  amount_loss: undefined,
  status: ""
});

export default {
  name: "EmployerClaimDialog",
  props: {
    visible: { type: Boolean, default: false },
    claimId: { type: [Number, String], default: "" },
    mode: { type: String, default: "add" }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      form: emptyForm(),
      order: {},
      rules: {
        policy_no: [
          { required: true, message: "请输入保单号", trigger: "blur" }
        ],
        employee_name: [
          { required: true, message: "请输入出险员工姓名", trigger: "blur" }
        ],
        employee_type: [
          { required: true, message: "请选择员工类别", trigger: "change" }
        ],
        status: [
          { required: true, message: "请选择案件状态", trigger: "change" }
        ]
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      }
    },
    readonly() {
      return this.mode === "view";
    },
    title() {
      return this.mode === "add"
        ? "录入出险员工"
        : this.mode === "edit"
        ? "编辑出险员工"
        : "查看出险员工";
    }
  },
  watch: {
    visible(value) {
      if (value && this.mode !== "add") this.loadClaim();
    }
  },
  methods: {
    async loadClaim() {
      this.loading = true;
      try {
        const res = await getEmployerClaimList({ id: this.claimId });
        const row = (res && res.rows && res.rows[0]) || {};
        const typeMap = { 搬运工人: 1, 文员内勤: 2, 司机押运: 4 };
        this.order = row;
        this.form = {
          ...emptyForm(),
          ...row,
          id: Number(row.id),
          order_id: row.order_id,
          employee_type:
            Number(row.employee_type) || typeMap[row.employee_type] || "",
          status: Number(row.status) || "",
          amount_paid: this.toYuan(row.amount_paid),
          amount_loss: this.toYuan(row.amount_loss),
          accident_date: row.accident_date
            ? dayjs(row.accident_date).format("YYYY-MM-DDTHH:mm:ssZ")
            : ""
        };
      } finally {
        this.loading = false;
      }
    },
    async loadOrderByPolicyNo() {
      if (!this.form.policy_no || this.readonly) return;
      const res = await getEmployerOrderList({
        policy_no: this.form.policy_no,
        staging: false
      });
      const row = res && res.rows && res.rows[0];
      if (!row) {
        this.order = {};
        this.form.order_id = "";
        this.$message.warning("未查询到该保单号的相关信息");
        return;
      }
      this.order = row;
      this.form.order_id = row.id;
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        if (!this.form.order_id) {
          this.$message.warning("请先输入有效保单号");
          return;
        }
        this.submitting = true;
        try {
          const res = await saveEmployerClaim({
            id: this.mode === "edit" ? Number(this.form.id) : 0,
            order_id: Number(this.form.order_id),
            employee_name: this.form.employee_name,
            employee_type: Number(this.form.employee_type),
            status: Number(this.form.status),
            accident_date: this.form.accident_date,
            amount_paid:
              this.form.amount_paid == null
                ? undefined
                : Math.round(this.form.amount_paid * 100),
            amount_loss:
              this.form.amount_loss == null
                ? undefined
                : Math.round(this.form.amount_loss * 100),
            accident_note: this.form.accident_note
          });
          if (!res) return;
          this.$message.success("保存成功");
          this.dialogVisible = false;
          this.$emit("success");
        } finally {
          this.submitting = false;
        }
      });
    },
    reset() {
      this.form = emptyForm();
      this.order = {};
      if (this.$refs.form) this.$refs.form.clearValidate();
    },
    toYuan(value) {
      return value === "" || value == null ? undefined : Number(value) / 100;
    },
    money(value) {
      return value === "" || value == null
        ? "-"
        : `${(Number(value) / 100).toFixed(2)}元`;
    },
    formatDate(value) {
      return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
    },
    orderStatus(value) {
      return (
        {
          0: "未知",
          1: "未支付",
          2: "已取消",
          4: "已支付",
          8: "已过期",
          16: "理赔中",
          32: "理赔完成"
        }[Number(value)] ||
        value ||
        "-"
      );
    }
  }
};
</script>

<style lang="less" scoped>
.order-summary {
  margin-bottom: 20px;
}
</style>
